import { useCallback } from 'react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { type BookingPriorityCountry } from '@/api/types';
import { IcDrag, IcTrash } from '@/components/ui/icons';
import styles from './PriorityDnDList.module.css';

interface PriorityDnDListProps {
  items: BookingPriorityCountry[];
  onChange: (items: BookingPriorityCountry[]) => void;
  onRemove?: (code: string) => void;
}

export function PriorityDnDList({ items, onChange, onRemove }: PriorityDnDListProps): JSX.Element {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = useCallback(
    (e: DragEndEvent) => {
      const { active, over } = e;
      if (!over || active.id === over.id) return;
      const oldIdx = items.findIndex((x) => x.country_code === active.id);
      const newIdx = items.findIndex((x) => x.country_code === over.id);
      if (oldIdx === -1 || newIdx === -1) return;
      const next = arrayMove(items, oldIdx, newIdx).map((it, i) => ({ ...it, position: i }));
      onChange(next);
    },
    [items, onChange],
  );

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext
        items={items.map((i) => i.country_code)}
        strategy={verticalListSortingStrategy}
      >
        <ul className={styles.list}>
          {items.map((it, i) => (
            <SortableRow
              key={it.country_code}
              item={it}
              index={i}
              onRemove={onRemove ? () => onRemove(it.country_code) : undefined}
            />
          ))}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

function SortableRow({
  item,
  index,
  onRemove,
}: {
  item: BookingPriorityCountry;
  index: number;
  onRemove?: () => void;
}): JSX.Element {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.country_code,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <li ref={setNodeRef} style={style} className={styles.row}>
      <span className={styles.position}>{index + 1}</span>
      <span className={styles.name}>{item.country_name} ({item.country_code})</span>
      {onRemove && (
        <button type="button" onClick={onRemove} aria-label="Remove" className={styles.icon}>
          <IcTrash />
        </button>
      )}
      <button
        type="button"
        className={styles.drag}
        aria-label="Drag"
        {...attributes}
        {...listeners}
      >
        <IcDrag />
      </button>
    </li>
  );
}
